import { Component, Input, OnChanges, OnInit, SimpleChanges } from '@angular/core';
import { untilDestroyed } from '@ngneat/until-destroy';
import { Store } from '@ngrx/store';
import { IdentityService } from 'src/app/core/identity.service';
import { FollowupStatusEnum } from 'src/app/recruiting/candidate-followups/models/followup-status.enum';
import { openConfirmationDialog } from 'src/app/shared/actions/confirmation-dialog.actions';
import { ConfirmationDialogSettings } from 'src/app/shared/models/confirmation-dialog-settings.model';
import { FollowUpViewBase } from 'src/app/shared/models/followup-base-view.model';
import { FollowupPickerBaseComponent } from 'src/app/shared/components/followup-picker/followup-picker-base.component';
import { showToasterMessage } from 'src/app/shared/actions/toaster.actions';
import { RecruitingService } from 'src/app/recruiting/recruiting.service';
import { FollowupTime } from 'src/app/shared/models/followup-time.model';
import dayjs from 'dayjs';
import { LDFeatureManager } from 'src/app/shared/services/ld-feature-manager';

@Component({
    selector: 'followup-picker-profile-base',
    template: '<div></div>',
    styles: []
})
export abstract class FollowupPickerProfileBaseComponent
    extends FollowupPickerBaseComponent
    implements OnInit, OnChanges
{
    @Input() showLabel = true;
    @Input() showFollowupReason = false;

    followUpId?: number = null;
    followupStatusEnum = FollowupStatusEnum;

    constructor(
        private readonly recruitingService: RecruitingService,
        private readonly identityService: IdentityService,
        private readonly _store: Store,
        protected readonly _featureManager: LDFeatureManager
    ) {
        super(_store, _featureManager);
    }

    ngOnInit(): void {
        this.recruiterId = this.identityService.current.contactId;
        this.initForm();
        this.initLookups(true);
        this.formValidSubscribe();
        this.selectedDates.push(this.currentDate);
        this.handleMarkAsCompleteConfirmationResult();
    }

    ngOnChanges(changes: SimpleChanges): void {
        if (changes?.currentFollowup?.currentValue) {
            this.changeInProgress = true;
            this.patchData(this.currentFollowup);
            this.changeInProgress = false;
        }
        this.checkCanEdit();
    }

    openMarkAsCompleteDialog(): void {
        const settings = {
            title: 'Complete Follow-Up',
            message:
                'Are you sure you want to complete this follow-up? The date, time, priority, and reason will be reset.',
            action: 'MARK AS COMPLETE',
            cancelText: 'CANCEL'
        } as ConfirmationDialogSettings;

        this._store.dispatch(openConfirmationDialog({ settings: settings }));
    }

    toggleCalendar(e?: MouseEvent): void {
        if (e) {
            e.preventDefault();
            e.stopPropagation();
        }
        this.showCalendar = !this.showCalendar;

        // This check allows the followup-summary endpoint to be called
        // at the right time since we need to lazy load followup data
        if (this.showCalendar) {
            this.initLookups(false);
        }
    }

    onReasonBlur(): void {
        if (this.resourcesForm.valid && this.resourcesForm.controls.followupReason.dirty) {
            this.saveFollowUp();
        }
    }

    protected getFollowUpTimes(): void {
        if (this.hours.length === 1) {
            this.recruitingService.getFollowupTimeEntries().subscribe((time: FollowupTime[]) => {
                this.setFollowUpTimes(time);
                this.patchData(this.currentFollowup);
            });
        } else {
            this.patchData(this.currentFollowup);
        }
    }

    protected patchDataToForm(followUp: FollowUpViewBase, priorIndex: number, timeIndex: number) {
        const date = dayjs(followUp.followupDate).format('MM/DD/YYYY');
        // Set data to form
        this.resourcesForm.get('priorityList').patchValue(this.priorityList[priorIndex]);
        this.resourcesForm.get('time').patchValue(this.hours[timeIndex]);
        this.resourcesForm.get('date').patchValue(date);
        this.resourcesForm.get('followupReason').patchValue(followUp.followupReason);
        // Set data to calendar
        this.selectedDates = [];
        this.selectedDates.push(new Date(date));
    }

    /**
     * Subscribe to check status form 'VALID' || 'INVALID'
     */
    protected formValidSubscribe(): void {
        this.resourcesForm.statusChanges.pipe(untilDestroyed(this)).subscribe((result) => {
            if (
                result === 'VALID' &&
                (this.resourcesForm.controls.priorityList.dirty ||
                    this.resourcesForm.controls.time.dirty ||
                    this.resourcesForm.controls.date.dirty)
            ) {
                this.saveFollowUp();
            }
        });
    }

    protected handleSave(data: FollowUpViewBase, markComplete = false): void {
        this.followUpId = data.id;
        this.animate();
        if (markComplete) {
            this._store.dispatch(
                showToasterMessage({
                    message: '1 followup has been completed.'
                })
            );
            this.resetDefaultValues();
        }
        this.resourcesForm.markAsPristine();
        this.saveInProgress = false;
        this.currentFollowup = data;
        this.onChangeFollowup.emit([data]);
    }

    // /**
    //  * Animation Saved
    //  */
    protected animate(): void {
        this.currentState = 'final';
        setTimeout(() => {
            this.currentState = 'initial';
        }, 2000);
    }

    abstract patchData(followUp: FollowUpViewBase): void;
    abstract saveFollowUp(markComplete?: boolean): void;
    abstract isMarkedComplete(): boolean;
    abstract handleMarkAsCompleteConfirmationResult(): void;
}
